import { axiosClient } from '../../../library/Service/axiosClient';

const BASE = process.env.REACT_APP_INTERNAL_ADMIN_SERVER;

/**
 * STANDARD ALUMNI ROW SHAPE — returned by every Layer 1/2/3 card, list and summary endpoint.
 * The frontend renders the same `AlumniRow` component regardless of which layer it came from.
 * `company_title` is intentionally absent here (it appears only in CSV exports).
 */
export interface StandardAlumniRow {
    student_id: string;
    name: string | null;
    program_name: string | null;
    pass_out_year: number | null;
    program_and_year: string;
    // Engagement signals surfaced as the 3 row chips. Stored as 'High' | 'Moderate' | 'Low'
    // (or null when unscored) — rendered as the first letter H / M / L in AlumniRow.
    alma_mater_pride: string | null;
    marketability: string | null;
    vibrancy: string | null;
    evidence_text: string | null;
}

export interface SignalPoolCardData {
    pool: string;
    icon: string;
    label: string;
    description: string;
    count: number;
    total_count_for_link: number;
    top_alumni: StandardAlumniRow[];
    // v1: activated_pct / contactability_pct intentionally not returned (progress bars deferred).
}

export interface SignalSummary {
    institute_id: number;
    filter_institute_id: number | null;
    scope_institute_ids: number[];
    pools: SignalPoolCardData[];
    computed_at: string;
}

export interface StrategicWatchlistCount {
    watchlist_code: string;
    label: string;
    strategy: string;
    description: string;
    count: number;
}

export interface StrategicSummary {
    outcome: string;
    institute_id: number;
    filter_institute_id: number | null;
    scope_institute_ids: number[];
    total: number;
    watchlists: StrategicWatchlistCount[];
    computed_at: string;
}

export interface StrategicList {
    outcome: string;
    watchlist_code: string;
    label: string;
    strategy: string;
    description: string;
    institute_id: number;
    filter_institute_id: number | null;
    total: number;
    limit: number;
    offset: number;
    alumni: StandardAlumniRow[];
}

export interface StrategicOutcomeTab {
    key: string;
    goal_code: string;
    label: string;
}

export interface StrategicOutcomesResponse {
    institute_id: number;
    commitment_paths: string[];
    outcomes: StrategicOutcomeTab[];
}

export interface ActivationStage {
    code: string;
    label: string;
    type: 'standard' | 'value' | 'dormant' | 'denied';
    count: number;
}

export interface ActivationSummary {
    outcome: string;
    label: string;
    institute_id: number;
    filter_institute_id: number | null;
    scope_institute_ids: number[];
    total_in_outcome: number;
    stages: ActivationStage[];
    value_zone: { count: number; pct: number; stages: string[] };
    top_alumni: StandardAlumniRow[];
    computed_at: string;
}

export interface ActivationOutcomeStages {
    outcome: string;
    label: string;
    total_in_outcome: number;
    stages: ActivationStage[];
    value_zone: { count: number; pct: number };
}

export interface ActivationCrossSummary {
    institute_id: number;
    filter_institute_id: number | null;
    outcomes: ActivationOutcomeStages[];
    computed_at: string;
}

export interface VelocityMonth {
    key: string;
    label: string;
}

export interface VelocityOutcomeSeries {
    outcome: string;
    label: string;
    data: number[];
}

export interface ActivationVelocity {
    institute_id: number;
    filter_institute_id: number | null;
    from_month: string;
    to_month: string;
    months: VelocityMonth[];
    outcomes: VelocityOutcomeSeries[];
    computed_at: string;
}

export interface ActivityEntryRow extends StandardAlumniRow {
    event_date: string;
    layer: string;
    watchlist_code: string;
    watchlist_label: string;
    outcome: string | null;
    stage: string | null;
    exit_reason?: string;
    exit_reason_label?: string;
    detected_at: string;
}

export interface ActivityLogResponse {
    institute_id: number;
    total: number;
    limit: number;
    offset: number;
    entries: ActivityEntryRow[];
}

export interface StageMovementRow extends StandardAlumniRow {
    transition_date: string;
    outcome: string;
    from_stage: string | null;
    from_stage_label: string | null;
    to_stage: string;
    to_stage_label: string;
    direction: string;
    detected_at: string;
}

export interface StageMovementResponse {
    institute_id: number;
    total: number;
    limit: number;
    offset: number;
    entries: StageMovementRow[];
}

export interface CustomWatchlistStage {
    code: string;
    label: string;
    type: 'standard' | 'value' | 'dormant' | 'denied';
    count: number;
}

export interface CustomWatchlistCardData {
    id: number;
    name: string;
    description: string | null;
    layer_context: string;
    tag: string;
    created_by: string;
    created_by_name: string | null;
    created_at: string;
    count: number;
    stages?: CustomWatchlistStage[];
    value_zone?: { count: number; pct: number; stages: string[] };
    top_alumni: StandardAlumniRow[];
}

export interface CustomWatchlistListResponse {
    institute_id: number;
    filter_institute_id: number | null;
    scope_institute_ids: number[];
    watchlists: CustomWatchlistCardData[];
}

export interface CustomAlumniSearchResult extends StandardAlumniRow {
    email: string | null;
}

export interface BulkResolveMatch extends CustomAlumniSearchResult {
    matched_on: string;
}

export interface BulkResolveResult {
    matched: BulkResolveMatch[];
    unmatched: string[];
    requested: number;
}

// ── Stage 6 — Dashboard Header ──

export interface InstituteContextCampus {
    id: number;
    name: string;
}

export interface InstituteContext {
    institute_id: number;
    institute_name: string | null;
    institute_group_id: number | null;
    institute_group_name: string | null;
    is_group: boolean;
    campuses: InstituteContextCampus[];
    commitment_paths: string[];
    enabled_outcomes: StrategicOutcomeTab[];
}

export interface DashboardHeaderCard {
    key: string;
    label: string;
    icon: string;
    value: number;
    sublabel: string;
    pct_of_total: number | null;
}

export interface DashboardHeader {
    institute_id: number;
    filter_institute_id: number | null;
    scope_institute_ids: number[];
    window_days: number;
    cards: DashboardHeaderCard[];
    computed_at: string;
}

class WatchlistDashboardService {
    // Layer 1 — all 6 signal pool cards for an institute (group-aggregated unless filter is passed).
    static getSignalSummary(instituteId: string | number, filterInstituteId?: string | number | null): Promise<SignalSummary> {
        const params: Record<string, any> = { institute_id: instituteId };
        if (filterInstituteId) params.filter_institute_id = filterInstituteId;
        return axiosClient()
            .get(`${BASE}/watchlists/signal/summary`, { params })
            .then((res) => res.data as SignalSummary);
    }

    // Single pool card (used by drill-down / refresh).
    static getSignalPool(pool: string, instituteId: string | number, filterInstituteId?: string | number | null): Promise<SignalPoolCardData> {
        const params: Record<string, any> = { institute_id: instituteId };
        if (filterInstituteId) params.filter_institute_id = filterInstituteId;
        return axiosClient()
            .get(`${BASE}/watchlists/signal/${pool}`, { params })
            .then((res) => res.data as SignalPoolCardData);
    }

    // Direct CSV export URL (the only response carrying company_title).
    static signalExportUrl(pool: string, instituteId: string | number): string {
        return `${BASE}/watchlists/signal/${pool}/export?institute_id=${instituteId}`;
    }

    // ── Layer 2 — Strategic ──

    // Layer 2 tabs enabled for this institute (from institute.commitment_paths ∩ implemented outcomes).
    static getStrategicOutcomes(instituteId: string | number): Promise<StrategicOutcomesResponse> {
        return axiosClient()
            .get(`${BASE}/watchlists/strategic-outcomes`, { params: { institute_id: instituteId } })
            .then((res) => res.data as StrategicOutcomesResponse);
    }

    static getStrategicSummary(outcome: string, instituteId: string | number, filterInstituteId?: string | number | null): Promise<StrategicSummary> {
        const params: Record<string, any> = { institute_id: instituteId };
        if (filterInstituteId) params.filter_institute_id = filterInstituteId;
        return axiosClient()
            .get(`${BASE}/watchlists/${outcome}/summary`, { params })
            .then((res) => res.data as StrategicSummary);
    }

    // One watchlist's paginated list (also returns `total`, used as the card count + top alumni).
    static getStrategicList(
        outcome: string,
        watchlistCode: string,
        instituteId: string | number,
        opts: { limit?: number; offset?: number; filterInstituteId?: string | number | null } = {}
    ): Promise<StrategicList> {
        const params: Record<string, any> = { institute_id: instituteId };
        if (opts.filterInstituteId) params.filter_institute_id = opts.filterInstituteId;
        if (opts.limit != null) params.limit = opts.limit;
        if (opts.offset != null) params.offset = opts.offset;
        return axiosClient()
            .get(`${BASE}/watchlists/${outcome}/${watchlistCode}`, { params })
            .then((res) => res.data as StrategicList);
    }

    static strategicExportUrl(outcome: string, watchlistCode: string, instituteId: string | number): string {
        return `${BASE}/watchlists/${outcome}/${watchlistCode}/export?institute_id=${instituteId}`;
    }

    // ── Layer 3 — Activation ──

    static getActivationSummary(outcome: string, instituteId: string | number, filterInstituteId?: string | number | null): Promise<ActivationSummary> {
        const params: Record<string, any> = { institute_id: instituteId };
        if (filterInstituteId) params.filter_institute_id = filterInstituteId;
        return axiosClient()
            .get(`${BASE}/activation/${outcome}/summary`, { params })
            .then((res) => res.data as ActivationSummary);
    }

    // Cross-outcome stage distribution (powers the Activation Pipeline bar chart).
    static getActivationCrossSummary(instituteId: string | number, filterInstituteId?: string | number | null): Promise<ActivationCrossSummary> {
        const params: Record<string, any> = { institute_id: instituteId };
        if (filterInstituteId) params.filter_institute_id = filterInstituteId;
        return axiosClient().get(`${BASE}/activation/summary`, { params }).then((res) => res.data as ActivationCrossSummary);
    }

    // Monthly forward stage-advance counts per outcome (powers the Stage Advance Velocity chart).
    static getActivationVelocity(instituteId: string | number, opts: { months?: number; filterInstituteId?: string | number | null } = {}): Promise<ActivationVelocity> {
        const params: Record<string, any> = { institute_id: instituteId };
        if (opts.months) params.months = opts.months;
        if (opts.filterInstituteId) params.filter_institute_id = opts.filterInstituteId;
        return axiosClient().get(`${BASE}/activation/velocity`, { params }).then((res) => res.data as ActivationVelocity);
    }

    static activationStageExportUrl(outcome: string, stage: string, instituteId: string | number): string {
        return `${BASE}/activation/${outcome}/${stage}/export?institute_id=${instituteId}`;
    }

    // ── Stage 4 — Activity Logs ──

    static getActivityEntries(instituteId: string | number, opts: { limit?: number; offset?: number; filterInstituteId?: string | number | null; layer?: string | null; fromDate?: string | null; toDate?: string | null } = {}): Promise<ActivityLogResponse> {
        const params: Record<string, any> = { institute_id: instituteId, limit: opts.limit };
        if (opts.offset != null) params.offset = opts.offset;
        if (opts.filterInstituteId) params.filter_institute_id = opts.filterInstituteId;
        if (opts.layer && opts.layer !== 'all') params.layer = opts.layer;
        if (opts.fromDate) params.from_date = opts.fromDate;
        if (opts.toDate) params.to_date = opts.toDate;
        return axiosClient().get(`${BASE}/activity-logs/entries`, { params }).then((r) => r.data as ActivityLogResponse);
    }

    static getActivityExits(instituteId: string | number, opts: { limit?: number; offset?: number; filterInstituteId?: string | number | null; layer?: string | null; fromDate?: string | null; toDate?: string | null } = {}): Promise<ActivityLogResponse> {
        const params: Record<string, any> = { institute_id: instituteId, limit: opts.limit };
        if (opts.offset != null) params.offset = opts.offset;
        if (opts.filterInstituteId) params.filter_institute_id = opts.filterInstituteId;
        if (opts.layer && opts.layer !== 'all') params.layer = opts.layer;
        if (opts.fromDate) params.from_date = opts.fromDate;
        if (opts.toDate) params.to_date = opts.toDate;
        return axiosClient().get(`${BASE}/activity-logs/exits`, { params }).then((r) => r.data as ActivityLogResponse);
    }

    static getActivityStageMovements(instituteId: string | number, opts: { limit?: number; offset?: number; filterInstituteId?: string | number | null; fromDate?: string | null; toDate?: string | null } = {}): Promise<StageMovementResponse> {
        const params: Record<string, any> = { institute_id: instituteId, limit: opts.limit };
        if (opts.offset != null) params.offset = opts.offset;
        if (opts.filterInstituteId) params.filter_institute_id = opts.filterInstituteId;
        if (opts.fromDate) params.from_date = opts.fromDate;
        if (opts.toDate) params.to_date = opts.toDate;
        return axiosClient().get(`${BASE}/activity-logs/stage-movements`, { params }).then((r) => r.data as StageMovementResponse);
    }

    // ── Stage 5 — Custom Watchlists ──

    static getCustomWatchlists(instituteId: string | number, filterInstituteId?: string | number | null): Promise<CustomWatchlistListResponse> {
        const params: Record<string, any> = { institute_id: instituteId };
        if (filterInstituteId) params.filter_institute_id = filterInstituteId;
        return axiosClient().get(`${BASE}/watchlists/custom`, { params }).then((r) => r.data as CustomWatchlistListResponse);
    }

    static searchCustomAlumni(instituteId: string | number, q: string, filterInstituteId?: string | number | null): Promise<{ results: CustomAlumniSearchResult[] }> {
        const params: Record<string, any> = { institute_id: instituteId, q };
        if (filterInstituteId) params.filter_institute_id = filterInstituteId;
        return axiosClient().get(`${BASE}/watchlists/custom/search-alumni`, { params }).then((r) => r.data);
    }

    // Resolve a bulk list of emails/names (from a pasted/uploaded CSV) to in-scope alumni.
    static resolveCustomAlumni(instituteId: string | number, identifiers: string[], filterInstituteId?: string | number | null): Promise<BulkResolveResult> {
        const params: Record<string, any> = { institute_id: instituteId };
        if (filterInstituteId) params.filter_institute_id = filterInstituteId;
        return axiosClient().post(`${BASE}/watchlists/custom/resolve-alumni`, { identifiers }, { params }).then((r) => r.data as BulkResolveResult);
    }

    static createCustomWatchlist(instituteId: string | number, body: { name: string; layer_context: string; description?: string }): Promise<{ id: number }> {
        return axiosClient().post(`${BASE}/watchlists/custom`, body, { params: { institute_id: instituteId } }).then((r) => r.data);
    }

    static addCustomMembers(instituteId: string | number, id: number, studentIds: string[], filterInstituteId?: string | number | null): Promise<{ added: number }> {
        const params: Record<string, any> = { institute_id: instituteId };
        if (filterInstituteId) params.filter_institute_id = filterInstituteId;
        return axiosClient().post(`${BASE}/watchlists/custom/${id}/members`, { student_ids: studentIds }, { params }).then((r) => r.data);
    }

    static getCustomMembers(instituteId: string | number, id: number, filterInstituteId?: string | number | null): Promise<{ id: number; name: string; alumni: StandardAlumniRow[]; total: number }> {
        const params: Record<string, any> = { institute_id: instituteId, limit: 100 };
        if (filterInstituteId) params.filter_institute_id = filterInstituteId;
        return axiosClient().get(`${BASE}/watchlists/custom/${id}/members`, { params }).then((r) => r.data);
    }

    static removeCustomMember(instituteId: string | number, id: number, studentId: string, filterInstituteId?: string | number | null): Promise<{ removed: number }> {
        const params: Record<string, any> = { institute_id: instituteId };
        if (filterInstituteId) params.filter_institute_id = filterInstituteId;
        return axiosClient().delete(`${BASE}/watchlists/custom/${id}/members/${studentId}`, { params }).then((r) => r.data);
    }

    static updateCustomWatchlist(instituteId: string | number, id: number, body: { name?: string; layer_context?: string; description?: string }, filterInstituteId?: string | number | null): Promise<{ id: number; updated: boolean }> {
        const params: Record<string, any> = { institute_id: instituteId };
        if (filterInstituteId) params.filter_institute_id = filterInstituteId;
        return axiosClient().patch(`${BASE}/watchlists/custom/${id}`, body, { params }).then((r) => r.data);
    }

    static deleteCustomWatchlist(instituteId: string | number, id: number, filterInstituteId?: string | number | null): Promise<{ id: number; deleted: boolean }> {
        const params: Record<string, any> = { institute_id: instituteId };
        if (filterInstituteId) params.filter_institute_id = filterInstituteId;
        return axiosClient().delete(`${BASE}/watchlists/custom/${id}`, { params }).then((r) => r.data);
    }

    // ── Stage 6 — Dashboard Header ──

    static getInstituteContext(instituteId: string | number): Promise<InstituteContext> {
        return axiosClient().get(`${BASE}/watchlists/institute-context`, { params: { institute_id: instituteId } }).then((r) => r.data as InstituteContext);
    }

    static getDashboardHeader(instituteId: string | number, filterInstituteId?: string | number | null): Promise<DashboardHeader> {
        const params: Record<string, any> = { institute_id: instituteId };
        if (filterInstituteId) params.filter_institute_id = filterInstituteId;
        return axiosClient().get(`${BASE}/watchlists/dashboard-header`, { params }).then((r) => r.data as DashboardHeader);
    }
}

export default WatchlistDashboardService;
